import { ConfigurationSection } from '@/models/configuration/Section';
import { TableDataSet } from '@/models/tableDataset/TableDataSet';
import { ApiIdentifers } from '../config/index';
import { TableDataSetField } from '@/models/tableDataset/TableDataSetField';
import { ConfigurationField } from '@/models/configuration/Field';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { LoggerService, UnitsHelper } from '@/utils';
import { UserProfile } from '@/models/dexie/UserProfile';
import { UserProfileStore } from '@/store/store.userProfile';
import { ConfigurationService } from '@/services/index';
import { DataSet } from '@/models/dataset/DataSet';
import { DataSetService } from './index';
import { Helpers } from '@/helpers/Helpers';
import countries from '@/assets/countries.json';

import vuetify from '@/plugins/vuetify';
import { saveAs } from 'file-saver';
import { Dictionary } from 'vue-router/types/router';
import { TableDataSetSection } from '@/models/tableDataset/TableDataSetSection';
import { ExcelExportDatasetsData } from '@/models/export/ExcelExportDatasetsData';
import { ExcelExportIdentifierListItem } from '@/models/export/ExcelExportIdentifierList';

import { ValueType, Workbook } from 'exceljs';

const firstDataFieldValueIndex = 6;
const firstDataRowIndex = 6; // the are rows added "manual": tabId, createdBy, createdAt, editedBy, editedAt, myMill date
const maxHeaderDeep = 3; // max number of header row before a value is named, MTT1(0) in MTT Licker-in Side

class ExcelExportProvider {
    public confData: ConfigurationSection[] = [];
    private seperator: string = ';';

    get getCompleteConfiguration() {
        return ConfigurationService.configuration;
    }

    get getConfiguration() {
        return ConfigurationService.configuration;
    }

    get getMachinesConfiguration() {
        return ConfigurationService.machinesConfiguration;
    }

    get getUnitsConfiguration() {
        return ConfigurationService.unitsConfiguration;
    }

    get getSamplesConfigruation() {
        return ConfigurationService.samplesConfiguration;
    }

    get getUserProfile(): UserProfile {
        return UserProfileStore.userProfile;
    }

    public async generateExcelFromDataSets(inDataSets?: DataSet[]): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                const data: TableDataSet[] = [];

                if (inDataSets !== undefined) {
                    inDataSets.forEach((ds) => {
                        data.push(DataSetService.GenerateTableDataSet(ds));
                    });
                } else {
                    DataSetService.GetFilteredDataSets.forEach((ds) => {
                        data.push(DataSetService.GenerateTableDataSet(ds));
                    });
                }
                this.generateExcelFromTableDataSets(data);
                resolve(true);
            } catch (error) {
                reject(error);
            }
        });
    }

    public async generateExcelFromTableDataSets(inDataSets: TableDataSet[]) {
        ConfigurationService.ClearUsedConfigurationSections();

        // array of arrays, see: https://github.com/sheetjs/sheetjs#array-of-arrays-input
        const excelData: Array<Array<string | number>> = [];

        /**
         * We need the number of columns per data set by the number of used
         * contacts, materials and samples. The highest number defines the
         * used columns.
         * Modified for the new way of displaying in excel. Set always to 1
         * since we show these now as separate rwo-blocks per contacts,
         * materials and samples. But we still need these identitifer list.
         */
        const dataSetColumns: Dictionary<number> = {};
        for (const inDataSet of inDataSets) {
            const countSections = inDataSet.sections.filter((sec) => sec.identifier === ApiIdentifers.contacts || sec.identifier === ApiIdentifers.material || sec.identifier === ApiIdentifers.sample);
            if (inDataSet.dataSetId != null) {
                dataSetColumns[inDataSet.dataSetId] = 1;
            }
        }

        /**
         * For new row-block displaying we need the maximal amount of
         * contacts, materials and samples.
         */
        const dataSetColumns2: Dictionary<number> = {};
        dataSetColumns2[ApiIdentifers.contacts] = this.getMaxSectionItems(inDataSets, ApiIdentifers.contacts);
        dataSetColumns2[ApiIdentifers.material] = this.getMaxSectionItems(inDataSets, ApiIdentifers.material);
        dataSetColumns2[ApiIdentifers.sample] = this.getMaxSectionItems(inDataSets, ApiIdentifers.sample);

        /**
         * List of extraction points used in samples.
         * Need to sort the samples correct over all datasets
         */
        const usedExtractionPoints: number[] = [];

        inDataSets.forEach((dataSet) => {
            /**
             * Sort materials by sorted descending by the percentage.
             * @see https://appsfactory.atlassian.net/jira/software/c/projects/TTDB/boards/695?modal=detail&selectedIssue=TTDB-758
             */
            const materials = dataSet.sections.find((sec) => sec.identifier === ApiIdentifers.material);
            if (materials != null && materials.fields.length > 1) {
                materials.fields.sort(this.sortMaterialsByBlendDesc);
                // now we have to rewrite sub-section identifier or sort will be useless...
                for (let materialIndex = 0; materialIndex < materials.fields.length; materialIndex++) {
                    materials.fields[materialIndex].identifier = `${materials.fields[materialIndex].identifier.split('__')[0]}__${materialIndex}`;
                }
            }

            /**
             * Filter samples to only show samples with labratory data
             * @see https://appsfactory.atlassian.net/jira/software/c/projects/TTDB/boards/695?modal=detail&selectedIssue=TTDB-758
             */
            const samples = dataSet.sections.find((sec) => sec.identifier === ApiIdentifers.sample);
            if (samples != null && samples.fields.length > 0) {
                const tmpSamples = samples.fields.filter((sample) => {
                    const labaratory = sample.fields && sample.fields.find((s) => s.identifier === ApiIdentifers.sampleLaboratory);
                    if (labaratory != null) {
                        return labaratory.fields && labaratory.fields.filter((f) => f.valueNumber != null).length > 0;
                    }

                    return false;
                });
                samples.fields = tmpSamples;

                // add sample identifier to list, if not already added
                samples.fields.forEach((sample) => {
                    const labaratory = sample.fields && sample.fields.find((s) => s.identifier === ApiIdentifers.sampleLaboratory);
                    if (labaratory != null) {
                        const extractionPoint = labaratory.fields && labaratory.fields.find((f) => f.identifier === ApiIdentifers.sampleLaboratoryExtractionPoint);
                        if (extractionPoint && extractionPoint.valueNumber != null && usedExtractionPoints.some((x) => x === extractionPoint.valueNumber) === false) {
                            usedExtractionPoints.push(extractionPoint.valueNumber);
                        }
                    }
                });

                // if all samples are filterd out, remove samples section
                if (samples.fields.length < 1) {
                    const index = dataSet.sections.findIndex((sec) => sec.identifier === ApiIdentifers.sample);
                    if (index >= 0) {
                        dataSet.sections.splice(index, 1);
                    }
                }
            }
        });

        usedExtractionPoints.sort();

        /**
         * Get creators and creation dates of data sets
         */
        const rowTabId: Array<string | number> = [];
        const rowCreatedBy: Array<string | number> = [];
        const rowCreatedAt: Array<string | number> = [];
        const rowEditedBy: Array<string | number> = [];
        const rowEditedAt: Array<string | number> = [];
        const rowMyMillDate: Array<string | number> = [];
        let fieldIndexHeader = firstDataFieldValueIndex;
        for (let inDataSetIndex = 0; inDataSetIndex < inDataSets.length; inDataSetIndex++) {
            const inDataSet = inDataSets[inDataSetIndex];
            if (inDataSetIndex === 0) {
                // add a string in index zero for bold text
                rowTabId[0] = `${vuetify.framework.lang.t('$vuetify.table.header.tabId')},bold`;
                rowCreatedBy[0] = `${vuetify.framework.lang.t('$vuetify.table.header.createdBy')},bold`;
                rowCreatedAt[0] = `${vuetify.framework.lang.t('$vuetify.table.header.createdAt')},bold`;
                rowEditedBy[0] = `${vuetify.framework.lang.t('$vuetify.table.header.editedBy')},bold`;
                rowEditedAt[0] = `${vuetify.framework.lang.t('$vuetify.table.header.editedAt')},bold`;
                rowMyMillDate[0] = `${vuetify.framework.lang.t('$vuetify.table.header.myMillDate')},bold`;
                rowTabId[firstDataFieldValueIndex - 2] = vuetify.framework.lang.t('$vuetify.table.header.tabId');
                rowCreatedBy[firstDataFieldValueIndex - 2] = vuetify.framework.lang.t('$vuetify.table.header.createdBy');
                rowCreatedAt[firstDataFieldValueIndex - 2] = vuetify.framework.lang.t('$vuetify.table.header.createdAt');
                rowEditedBy[firstDataFieldValueIndex - 2] = vuetify.framework.lang.t('$vuetify.table.header.editedBy');
                rowEditedAt[firstDataFieldValueIndex - 2] = vuetify.framework.lang.t('$vuetify.table.header.editedAt');
                rowMyMillDate[firstDataFieldValueIndex - 2] = vuetify.framework.lang.t('$vuetify.table.header.myMillDate');
            }
            fieldIndexHeader += inDataSetIndex > 0 ? dataSetColumns[inDataSets[inDataSetIndex - 1].dataSetId || 0] : 0;
            rowTabId[fieldIndexHeader] = inDataSet.dataSetId || '';
            rowCreatedBy[fieldIndexHeader] = inDataSet.created.userName;
            // rowCreatedAt[fieldIndexHeader] = `${Helpers.getExportDateTimeString(inDataSet.created.date)}`;
            rowCreatedAt[fieldIndexHeader] = inDataSet.created.date;
            rowEditedBy[fieldIndexHeader] = (inDataSet.lastModified && inDataSet.lastModified.userName) || '';
            rowEditedAt[fieldIndexHeader] = inDataSet.lastModified && inDataSet.lastModified.date ? inDataSet.lastModified.date : '';
            rowMyMillDate[fieldIndexHeader] = inDataSet.myMillImport && inDataSet.myMillImport.extractionDate ? inDataSet.myMillImport.extractionDate : '';
        }
        excelData.push(rowTabId);
        excelData.push(rowCreatedBy);
        excelData.push(rowCreatedAt);
        excelData.push(rowEditedBy);
        excelData.push(rowEditedAt);
        excelData.push(rowMyMillDate);

        /**
         * Go trought data-set section-wise.
         * First loop all generaal sections.
         * Exclude machineOrder section, since its only for add machine sections.
         */
        for (const generalSection of this.getConfiguration.sections.filter((sec) => sec.identifier !== ApiIdentifers.machineOrder)) {
            const dataSetsSections: ExcelExportDatasetsData[] = [];
            // iterate over all datasets to not miss empty data
            for (const dataSetid in dataSetColumns) {
                if (dataSetid) {
                    const dataSet = inDataSets.find((ds) => ds.dataSetId === dataSetid);
                    if (dataSet != null) {
                        const section = dataSet.sections.find((sec) => sec.identifier === generalSection.identifier);
                        dataSetsSections.push({ dataSetid, data: section });
                    }
                }
            }
            if (generalSection.identifier === ApiIdentifers.contacts) {
                // tslint:disable-next-line: prefer-for-of
                for (let subIndex = 0; subIndex < dataSetColumns2[ApiIdentifers.contacts]; subIndex++) {
                    this.generateRowData2(
                        generalSection,
                        inDataSets,
                        dataSetColumns,
                        excelData,
                        subIndex,
                        0,
                        undefined,
                        new ExcelExportIdentifierListItem({ id: generalSection.identifier, name: `${generalSection.name} ${subIndex + 1}` }),
                    );
                }
            } else if (generalSection.identifier === ApiIdentifers.material) {
                // tslint:disable-next-line: prefer-for-of
                for (let subIndex = 0; subIndex < dataSetColumns2[ApiIdentifers.material]; subIndex++) {
                    this.generateRowData2(
                        generalSection,
                        inDataSets,
                        dataSetColumns,
                        excelData,
                        subIndex,
                        0,
                        undefined,
                        new ExcelExportIdentifierListItem({ id: generalSection.identifier, name: `${generalSection.name} ${subIndex + 1}` }),
                    );
                }
            } else {
                this.generateRowData2(generalSection, inDataSets, dataSetColumns, excelData, -1);
            }
        }

        /**
         * Loop trough machine settings
         */
        for (const machineSection of this.getMachinesConfiguration.sections) {
            const dataSetsSections: ExcelExportDatasetsData[] = [];
            const selectedMachineSection = new ConfigurationSection(machineSection);
            // iterate over all datasets to not miss empty data
            for (const dataSetid in dataSetColumns) {
                if (dataSetid) {
                    const dataSet = inDataSets.find((ds) => ds.dataSetId === dataSetid);
                    if (dataSet != null) {
                        const section = dataSet.sections.find((sec) => sec.identifier === selectedMachineSection.identifier);
                        dataSetsSections.push({ dataSetid, data: section });
                    }
                }
            }
            this.generateRowData2(selectedMachineSection, inDataSets, dataSetColumns, excelData, -1);
        }

        /**
         * Loop trough samples
         */
        for (const samples of this.getSamplesConfigruation.sections) {
            const dataSetsSections: ExcelExportDatasetsData[] = [];
            // iterate over all datasets to not miss empty data
            for (const dataSetid in dataSetColumns) {
                if (dataSetid) {
                    const dataSet = inDataSets.find((ds) => ds.dataSetId === dataSetid);
                    if (dataSet != null) {
                        const section = dataSet.sections.find((sec) => sec.identifier === samples.identifier);
                        dataSetsSections.push({ dataSetid, data: section });
                    }
                }
            }
            // this.generateRowData(samples, inDataSets, dataSetColumns, excelData);

            /**
             * Check if need to add dummy sample sections to getr correct order in excel file
             */
            inDataSets.forEach((dataSet) => {
                const dataSetSamples = dataSet.sections.find((sec) => sec.identifier === ApiIdentifers.sample);
                if (dataSetSamples != null) {
                    const extractionPointsToAdd: number[] = [];
                    usedExtractionPoints.forEach((uep) => {
                        const sample = dataSetSamples.fields.find((s) => {
                            const labaratory = s.fields && s.fields.find((f) => f.identifier === ApiIdentifers.sampleLaboratory);
                            if (labaratory != null) {
                                const searchExtractionPoint = labaratory.fields && labaratory.fields.find((sf) => sf.identifier === ApiIdentifers.sampleLaboratoryExtractionPoint);
                                if (searchExtractionPoint && searchExtractionPoint.valueNumber === uep) {
                                    return true;
                                }
                            }
                            return false;
                        });
                        if (sample == null) {
                            extractionPointsToAdd.push(uep);
                        }
                    });
                    if (extractionPointsToAdd.length > 0) {
                        let index = dataSetSamples.fields.length;
                        extractionPointsToAdd.forEach((epta) => {
                            const newSample = new TableDataSetField(samples);
                            newSample.identifier = `${newSample.identifier}__${index}`;
                            newSample.type = FieldTypeEnum.ACTION_SUBFIELD;
                            const newLaboratory = newSample.fields && newSample.fields.find((f) => f.identifier === ApiIdentifers.sampleLaboratory);
                            if (newLaboratory != null) {
                                const newExtractionPoint = newLaboratory.fields && newLaboratory.fields.find((sf) => sf.identifier === ApiIdentifers.sampleLaboratoryExtractionPoint);
                                if (newExtractionPoint) {
                                    newExtractionPoint.valueNumber = epta;
                                }
                            }
                            dataSetSamples.fields.push(newSample);
                            index++;
                        });
                    }

                    // sort samples by extraction points and rename sub-section identifier
                    dataSetSamples.fields.sort(this.sortSamplesByExtractionPoint);
                    for (let sampleIndex = 0; sampleIndex < dataSetSamples.fields.length; sampleIndex++) {
                        const sample = dataSetSamples.fields[sampleIndex];
                        sample.identifier = `${sample.identifier.split('__')[0]}__${sampleIndex}`;
                    }
                }
            });

            for (let subIndex = 0; subIndex < dataSetColumns2[ApiIdentifers.sample]; subIndex++) {
                this.generateRowData2(
                    samples,
                    inDataSets,
                    dataSetColumns,
                    excelData,
                    subIndex,
                    0,
                    undefined,
                    new ExcelExportIdentifierListItem({ id: samples.identifier, name: `${samples.name} ${subIndex + 1}` }),
                );
            }
        }

        //   ExcelJs
        const workbook = new Workbook();
        let testSeriesName: string | undefined = '';

        try {
            testSeriesName = inDataSets[0].sections[0].fields[0].valueString;
            if (testSeriesName != null) {
                // worksheet name cannot include any of the following characters: * ? : \ /
                testSeriesName = testSeriesName.replace(/[\*\?\:\\\/]/g, '_');
            } else {
                testSeriesName = '';
            }
        } catch (error) {
            LoggerService.LogError(`error on get test series name: `, error);
        }

        const worksheet = workbook.addWorksheet(`Test Series - ${testSeriesName}`);
        let lastSection = '';
        const lastSections = new Array<string>(maxHeaderDeep);
        const lastSectionStartIndex = new Array<number>(maxHeaderDeep);
        for (let rowIndex = 0; rowIndex < excelData.length; rowIndex++) {
            const row = excelData[rowIndex];

            // fill empty indexes, autofill from exceljs seems to have some issues
            for (let indexAddEmptyCell = 0; indexAddEmptyCell < row.length; indexAddEmptyCell++) {
                if (row[indexAddEmptyCell] == null) {
                    row[indexAddEmptyCell] = '';
                }
            }

            // check if we are in a new (sub-)section to add bold font and merge section cells
            const sectionsString = (row[0] as string).substring(0, (row[0] as string).lastIndexOf(','));
            const sections = (row[0] as string).split(',');
            const startCellIndex = sections.length - 2;
            let bold = false;
            let useDate = false;
            /**
             * Check if we need to use Bold.
             * For the newer layout with materials/contacts/samples listet
             * in seperate row blocks, we need to add an extra check for materials/contatcs.
             */
            if (lastSection !== sectionsString || (row[1] as string).includes('Material') || (row[1] as string).includes('Contacts')) {
                lastSection = sectionsString;
                bold = true;
            }
            /**
             * Check for date fields
             */
            if (
                (row[0] as string).includes('Created at') ||
                (row[0] as string).includes('Modified at') ||
                (row[0] as string).includes('MyMill date') ||
                (row[0] as string).includes(ApiIdentifers.generalTrialDate)
            ) {
                useDate = true;
            }

            // remove first field with identifier list, just needed for formatting
            row.splice(0, 1);
            // add row
            worksheet.insertRow(rowIndex + 1, row);

            // add dates
            if (useDate === true) {
                try {
                    worksheet.getRow(rowIndex + 1).eachCell((cell, index) => {
                        if (index >= firstDataFieldValueIndex && cell.value !== '') {
                            cell.value = new Date(cell.value as string);
                        }
                    });
                } catch (error) {
                    LoggerService.LogError(`error on adding date cell in row ${rowIndex}: `, error);
                }
            }

            // add bold formatting
            if (bold === true) {
                try {
                    worksheet.getRow(rowIndex + 1).getCell(1).font = { bold: true };
                    worksheet.getRow(rowIndex + 1).getCell(2).font = { bold: true };
                    worksheet.getRow(rowIndex + 1).getCell(3).font = { bold: true };
                    if (rowIndex < firstDataRowIndex) {
                        worksheet.getRow(rowIndex + 1).getCell(4).font = { bold: true };
                    }
                    /**
                     * Preparation for mergin section header cells
                     */
                    // for (let indexSet = 0; indexSet < maxHeaderDeep; indexSet++) {
                    //     if (lastSections[indexSet] !== sections[indexSet]) {
                    //         if (rowIndex - lastSectionStartIndex[indexSet] > 1) {
                    //             if (lastSections[0] === ApiIdentifers.general) {
                    //                 if (indexSet < 1) {
                    //                     LoggerService.debug(
                    //                         `merge ${this.getIdentifierText(lastSections[indexSet], 'fail!')}: `,
                    //                         lastSectionStartIndex[indexSet],
                    //                         indexSet + 1,
                    //                         rowIndex,
                    //                         indexSet + 1
                    //                     );
                    //                     worksheet.mergeCells(lastSectionStartIndex[indexSet], indexSet + 1, rowIndex, indexSet + 1);
                    //                 }
                    //             } else if (lastSections[0] === ApiIdentifers.customer) {
                    //                 if (indexSet < 2) {
                    //                     LoggerService.debug(
                    //                         `merge ${this.getIdentifierText(lastSections[indexSet], 'fail!')}: `,
                    //                         lastSectionStartIndex[indexSet],
                    //                         indexSet + 1,
                    //                         rowIndex,
                    //                         indexSet + 1
                    //                     );
                    //                     worksheet.mergeCells(lastSectionStartIndex[indexSet], indexSet + 1, rowIndex, indexSet + 1);
                    //                 }
                    //             } else {
                    //                 LoggerService.debug(`merge ${this.getIdentifierText(lastSections[indexSet], 'fail!')}: `, lastSectionStartIndex[indexSet], indexSet + 1, rowIndex, indexSet + 1);
                    //                 worksheet.mergeCells(lastSectionStartIndex[indexSet], indexSet + 1, rowIndex, indexSet + 1);
                    //             }
                    //         }
                    //         lastSections[indexSet] = sections[indexSet];
                    //         lastSectionStartIndex[indexSet] = rowIndex + 1;
                    //     }
                    // }
                } catch (error) {
                    LoggerService.LogError(`error on formatting cell in row ${rowIndex}: `, error);
                }
            }

            // align all string to right to fit better with numbers
            // worksheet.getRow(rowIndex + 1).eachCell((cell, index) => {
            //     if (index >= firstDataFieldValueIndex && cell.value !== '' && cell.type === ValueType.String ) {
            //         cell.alignment = { horizontal: 'right' };
            //     }
            // });
        }

        // write to a stream
        workbook.xlsx.writeBuffer().then((data) => {
            const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, 'mytab-export.xlsx');
        });
    }

    /**
     * Method to generate data for one row
     *
     * @param inConfigurationField      Use configration field containing the fields meta data
     * @param inDataSets                All used data sets for export to fetch values
     * @param inDataSetColumns          List of used columns per data set object
     * @param excelData                 Array of array, each sub-array is the data for a row
     * @param actionIndex               Index for action sections
     * @param inDeep                    Iteration deep, just for debugging
     * @param identifierList            A list of identififers to know in which (sub-)section we are
     * @param inActionSectionId         If action section, then pass identififer to iterate over sub-data
     */
    private generateRowData2(
        inConfigurationField: ConfigurationField | ConfigurationSection,
        inDataSets: TableDataSet[],
        inDataSetColumns: Dictionary<number>,
        excelData: Array<Array<string | number>>,
        actionIndex: number,
        inDeep?: number,
        identifierList?: ExcelExportIdentifierListItem[],
        inActionSectionId?: ExcelExportIdentifierListItem,
    ): void {
        const deep = inDeep != null ? inDeep + 1 : 0;
        // deep should not be greater then 3 due to excel structure
        LoggerService.debug(`generateRowData2 deep: ${deep}`);

        if (inConfigurationField.fields != null) {
            const noUnitFields: ConfigurationField[] = [];

            /**
             * Make a copy to avoid removing unit fileds with the fitlering
             * of sub sections...
             */
            let inConfigurationFieldCopy: ConfigurationField | ConfigurationSection | undefined;
            if (inConfigurationField instanceof ConfigurationSection) {
                inConfigurationFieldCopy = new ConfigurationSection(inConfigurationField);
            } else {
                inConfigurationFieldCopy = new ConfigurationField(inConfigurationField);
            }

            if (inConfigurationFieldCopy != null && inConfigurationFieldCopy.fields != null) {
                inConfigurationFieldCopy.fields.filter(ExcelExportProvider.filterUnitFormatFields).forEach((cf) => {
                    noUnitFields.push(new ConfigurationField(cf));
                });
                this.sortSectionsFields(noUnitFields);

                let addSectionName = true;

                for (let index = 0; index < noUnitFields.length; index++) {
                    const configurationField = noUnitFields[index];
                    const fieldIdList = identifierList ? Object.assign([], identifierList) : [{ id: inConfigurationFieldCopy.identifier, name: inConfigurationFieldCopy.name }];
                    // `${identifierList ? identifierList : inConfigurationField.identifier},${configurationField.identifier}`;
                    if (configurationField.fields == null) {
                        // add new row
                        const rowData: Array<string | number> = [];
                        rowData[0] = `${fieldIdList.map((x) => x.id).join(',')},${configurationField.identifier}`;
                        if (index === 0 || addSectionName === true) {
                            for (let colIndex = 0; colIndex < fieldIdList.length; colIndex++) {
                                const element = fieldIdList[colIndex];
                                rowData[colIndex + 1] = this.getIdentifierText(element.id, element.name);
                                if (colIndex === 0 && (configurationField.action != null || inActionSectionId != null)) {
                                    rowData[colIndex + 1] = `${rowData[colIndex + 1]} ${actionIndex + 1}`;
                                }
                            }
                        }
                        rowData[firstDataFieldValueIndex - 2] = this.getIdentifierText(configurationField.identifier, configurationField.name);
                        rowData[firstDataFieldValueIndex - 1] = this.getUnitFormat(configurationField, inConfigurationField.fields || []);

                        //  get datasets field values
                        let lastDataSetOffset = 0;
                        let rowFieldIndex = firstDataFieldValueIndex + lastDataSetOffset;
                        /**
                         * Use inDataSets here to get correct order.
                         * Former used dictonry 'inDataSetColumns' is not sortet
                         * and data will end up in a wrong alignment of TabID's and sections.
                         * See Ticket TTDB-758 (https://appsfactory.atlassian.net/browse/TTDB-758?focusedCommentId=213074)
                         */
                        for (const inDataSet of inDataSets) {
                            const dataSetid = inDataSet.dataSetId;
                            if (dataSetid) {
                                const dataSet = inDataSets.find((ds) => ds.dataSetId === dataSetid);

                                if (dataSet != null) {
                                    rowFieldIndex += lastDataSetOffset;
                                    if (configurationField.action != null || inActionSectionId != null) {
                                        // handle action sections seperate, e.g. material
                                        const subFieldIdList = Object.assign([], fieldIdList);
                                        subFieldIdList.splice(
                                            1,
                                            0,
                                            new ExcelExportIdentifierListItem({
                                                id: `${inActionSectionId && inActionSectionId.id}__${actionIndex}`,
                                                name: `${inActionSectionId && inActionSectionId.name}__${actionIndex}`,
                                            }),
                                        );
                                        const subField = this.getDataSetData(dataSet, subFieldIdList);
                                        const fieldValue = this.getFieldValue((subField && subField.fields) || [], {
                                            fieldIdentifier: configurationField.identifier,
                                            subSectionIndex: actionIndex,
                                        });
                                        rowData[rowFieldIndex] = fieldValue != null ? fieldValue : '';
                                    } else {
                                        const field = this.getDataSetData(dataSet, fieldIdList);
                                        const fieldValue = this.getFieldValue((field && field.fields) || [], { fieldIdentifier: configurationField.identifier });
                                        rowData[rowFieldIndex] = fieldValue != null ? fieldValue : '';
                                    }
                                } else {
                                    // add empty columns if no dataSet found, as fallback
                                    rowData.concat(new Array(inDataSetColumns[dataSetid || 0]));
                                }
                                lastDataSetOffset = inDataSetColumns[dataSetid || ''];
                            }
                        }
                        if (this.checkIfRowHasData(rowData)) {
                            addSectionName = false;
                            excelData.push(rowData);
                        }
                    } else {
                        // go to next level
                        fieldIdList.push({ id: configurationField.identifier, name: configurationField.name });
                        const configurationFieldWithUnits = inConfigurationField!.fields!.find((icf) => icf.identifier === configurationField.identifier);
                        if (configurationFieldWithUnits != null) {
                            if (inConfigurationField.action != null) {
                                this.generateRowData2(
                                    configurationFieldWithUnits,
                                    inDataSets,
                                    inDataSetColumns,
                                    excelData,
                                    actionIndex,
                                    deep,
                                    fieldIdList,
                                    new ExcelExportIdentifierListItem({ id: inConfigurationField.identifier, name: inConfigurationField.name }),
                                );
                            } else {
                                this.generateRowData2(configurationFieldWithUnits, inDataSets, inDataSetColumns, excelData, actionIndex, deep, fieldIdList, inActionSectionId);
                            }
                        }
                    }
                }
            }
        } else {
            LoggerService.warn(`Trying to get Data from a field without subfields!`);
        }
    }

    /**
     * Method to fetch the (sub-)section out of a dataset to pass to value finder method
     *
     * @param inDataSet         A data set
     * @param inFieldIdList     A list of identififers to know in which (sub-)section we are
     * @returns                 A field object
     */
    private getDataSetData(inDataSet: TableDataSet, inFieldIdList: ExcelExportIdentifierListItem[]): TableDataSetField | TableDataSetSection | undefined {
        let fieldData: TableDataSetField | TableDataSetSection | undefined;

        for (let index = 0; index < inFieldIdList.length; index++) {
            const idItem = inFieldIdList[index];
            if (index === 0) {
                fieldData = inDataSet.sections.find((sec) => sec.identifier === idItem.id);
            } else {
                if (fieldData && fieldData.fields) {
                    fieldData = fieldData.fields.find((field) => field.identifier === idItem.id);
                }
            }
        }

        return fieldData;
    }

    /**
     * Method to check if a generated row has data values
     * @param inRodData     Array of values
     * @returns             True if contains data vlaues, else false
     */
    private checkIfRowHasData(inRodData: Array<string | number>): boolean {
        let hasData = false;
        for (let index = firstDataFieldValueIndex; index < inRodData.length; index++) {
            const element = inRodData[index];
            if (element != null && element !== '') {
                hasData = true;
                break;
            }
        }
        return hasData;
    }

    /**
     * Method to fetch a value from a data set (sub-)section field corresponding to a given configuration field
     * @param inFields  Array of field objects
     * @param params    A list of params
     * @returns         Value as string or null
     */
    private getFieldValue(
        inFields: TableDataSetField[] | ConfigurationField[],
        params: {
            fieldIdentifier: string;
            subSectionIndex?: number;
            isConfig?: boolean;
        },
    ): string | number | null {
        let fieldValue: string | number | null = null;

        const field = inFields.find((f) => f.identifier === params.fieldIdentifier);
        if (field == null) {
            for (const subField of inFields) {
                if (subField.fields != null) {
                    fieldValue = this.getFieldValue(subField.fields, params);
                    if (fieldValue != null) {
                        break;
                    }
                }
            }
        } else {
            if (field.type !== FieldTypeEnum.UNIT) {
                if (field.valueBool !== undefined) {
                    // TODO: Add translation for yes/no ?
                    fieldValue = field.valueBool === true ? 'yes' : 'no';
                } else if (field.valueNumber != null) {
                    if (field.type === FieldTypeEnum.SELECT && ((params && params.isConfig == null) || (params && params.isConfig != null && params.isConfig === false))) {
                        if (field.values !== undefined) {
                            /**
                             * Select value for select fields by id/value instead index.
                             * We need this since some fields have be changed in order and/or
                             * avaialable values.
                             */
                            const fieldValueObject = field.values.find((fv) => fv.value === field.valueNumber);
                            if (fieldValueObject) {
                                fieldValue = fieldValueObject.text;
                            } else {
                                /**
                                 * Just as fallback
                                 */
                                fieldValue = (field.values[field.valueNumber] || { text: field.valueNumber.toString() }).text;
                            }
                            /**
                             * Check if all selectable vales are valid numbers
                             * Credits: https://stackoverflow.com/a/32091115/2979067
                             * Additional we allow negativ numbers and #.0# numbers
                             */
                            const hasStringValues = field.values.filter((v) => v.text.match(/^(-?[1-9]\d*|-?0\.\d*[1-9]|-?[1-9]\d*\.\d*[0-9]|0)$/) == null);
                            if (hasStringValues.length === 0) {
                                fieldValue = parseFloat(fieldValue);
                            }
                        } else {
                            // since this is just a fallback number value should be fine here
                            fieldValue = field.valueNumber;
                        }
                    } else if (field.type === FieldTypeEnum.AXSELECT && ((params && params.isConfig == null) || (params && params.isConfig != null && params.isConfig === false))) {
                        if (field.axValues !== undefined) {
                            const axField = field.axValues.find((x) => x.value === field.valueString);
                            if (axField !== undefined) {
                                fieldValue = axField.text;
                            } else {
                                fieldValue = field.valueString || '';
                            }
                        } else {
                            fieldValue = field.valueString || '';
                        }
                    } else {
                        /**
                         * here we know the value should always be a number so we can parse back to float
                         * after value was calculatet or jsut set to fixed precision
                         */
                        if (field.unit !== undefined && field.unit !== ApiIdentifers.unitTemperature && params && inFields) {
                            const unitField = inFields.find((x) => x.identifier === field.unit);
                            if (unitField !== undefined && unitField.unitIdentifier !== undefined) {
                                const value = UnitsHelper.calcByUnit(unitField.unitIdentifier, field.rangeStep, field.valueNumber, unitField.valueNumber || undefined);
                                fieldValue = parseFloat(value);
                            }
                        } else if (field.unit !== undefined && field.unit === ApiIdentifers.unitTemperature) {
                            // handle temperature extra, format is selected by user profile
                            const value = UnitsHelper.calcByUnit(field.unit, field.rangeStep, field.valueNumber, 0);
                            fieldValue = parseFloat(value);
                        } else {
                            fieldValue = parseFloat(field.rangeStep ? field.valueNumber.toFixed(UnitsHelper.getPrecision(field.rangeStep)) : field.valueNumber.toString());
                        }
                    }
                } else if (field.valueString !== undefined) {
                    let value = '';
                    if (field.identifier === ApiIdentifers.generalTrialDate) {
                        // value = `${Helpers.getFormattedDateString(field.valueString)}`;
                        value = field.valueString;
                    } else if (field.identifier === ApiIdentifers.axCountry) {
                        const country = countries.find((c: any) => c.id === field.valueString);
                        if (country) {
                            value = country.name;
                        }
                    } else {
                        value = field.valueString;
                    }
                    fieldValue = `${value}`;
                }
            }
        }
        return fieldValue;
    }

    // tslint:disable-next-line: member-ordering
    private static filterUnitFormatFields(inField: ConfigurationField): any {
        if (inField.fields) {
            return (inField.fields = inField.fields.map((o) => Object.assign({}, o)).filter(ExcelExportProvider.filterUnitFormatFields)).length;
        } else if (inField.type !== FieldTypeEnum.UNIT) {
            return true;
        }
        return false;
    }

    /**
     * Helper method to fetch name and unit for a field
     * @param inField
     * @param inFields
     * @returns         A string with name and unit, e.g. 'Temperatur °C'
     */
    private getNameWithUnit(inField: ConfigurationField, inFields: ConfigurationField[]): string {
        let name = ''; // this.getIdentifierText(inField.identifier, inField.name);

        const unitFormatName = this.getUnitFormat(inField, inFields);
        if (unitFormatName !== '') {
            name = `${name}  ${unitFormatName}`;
        }

        return name;
    }

    /**
     * Helper method to fetch a unit for a field
     * @param field
     * @param inFields
     * @returns         A string with the unit
     */
    private getUnitFormat(field: ConfigurationField, inFields: ConfigurationField[]): string {
        let unitFormatName: string = '';

        if (field.type === FieldTypeEnum.NUMBER && field.unit !== undefined) {
            let unitFormatId: string = ApiIdentifers.unitTemperature; // handle temperature extra, format is selected by user profile
            if (field.unit !== ApiIdentifers.unitTemperature) {
                const unit = inFields.find((x) => x.identifier === field.unit);
                if (unit !== undefined && unit.unitIdentifier !== undefined) {
                    unitFormatId = unit.unitIdentifier;
                } else {
                    unitFormatId = '';
                }
            }
            if (unitFormatId.length > 0) {
                const unitFormat = this.getUnitsConfiguration.sections.find((sec) => sec.identifier === unitFormatId);
                if (unitFormat !== undefined && unitFormat.values !== undefined && this.getUserProfile !== undefined) {
                    const userProfileFormat = this.getUserProfile.units.find((upf) => upf.unitIdentifier === unitFormatId);
                    unitFormatName =
                        userProfileFormat !== undefined && userProfileFormat.selectedValue !== undefined
                            ? (unitFormat.values.find((v) => v.value === userProfileFormat.selectedValue) || { text: '-' }).text
                            : (unitFormat.values.find((v) => v.value === unitFormat.base) || unitFormat.values[0]).text;
                }
            }
        } else if (field.type === FieldTypeEnum.NUMBER && field.fixedUnit !== undefined) {
            unitFormatName = field.fixedUnit;
        }

        return unitFormatName;
    }

    /**
     * Helper method to fetch translation string for an identifier
     * @param identifier
     * @param name
     * @returns             Translation string or as fallback name of field
     */
    private getIdentifierText(identifier: string, name: string): string {
        let ret: string = '';
        ret = vuetify.framework.lang.t(`$vuetify.identifiers.${identifier}`);
        // fallback to name string if no translation found
        if (ret === undefined || ret.length === 0 || ret === identifier || ret.includes('$vuetify.identifiers.')) {
            ret = name;
            LoggerService.LogDebug(`getIdentifierText fallback text for: ${identifier} --> ${ret}`);
        }
        return ret;
    }

    private sortSectionsFields(sectionFields: ConfigurationField[]) {
        sectionFields.forEach((sectionField: ConfigurationField) => {
            if (sectionField.type === FieldTypeEnum.SUBFIELD && sectionField.fields) {
                this.sortSectionsFields(sectionField.fields);
            }
        });

        sectionFields.sort(this.sortFields);
    }

    private sortFields(lhs: ConfigurationField, rhs: ConfigurationField): number {
        if ((lhs.type === FieldTypeEnum.SUBFIELD || lhs.type === FieldTypeEnum.ACTION_SUBFIELD) && (rhs.type === FieldTypeEnum.SUBFIELD || rhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return 0;
        } else if (lhs.type !== FieldTypeEnum.SUBFIELD && lhs.type !== FieldTypeEnum.ACTION_SUBFIELD && (rhs.type === FieldTypeEnum.SUBFIELD || rhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return -1;
        } else if (rhs.type !== FieldTypeEnum.SUBFIELD && rhs.type !== FieldTypeEnum.ACTION_SUBFIELD && (lhs.type === FieldTypeEnum.SUBFIELD || lhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return 1;
        }
        return 1;
    }

    private getMaxSectionItems(inDatasets: TableDataSet[], inIdentifier: string): number {
        let maxSectionItems = 0;
        const usedSamplesExtractionPoints: number[] = [];
        for (const dataSet of inDatasets) {
            const section = dataSet.sections.find((sec) => sec.identifier === inIdentifier);
            if (section != null) {
                if (inIdentifier === ApiIdentifers.sample) {
                    section.fields.forEach((sample) => {
                        if (sample.fields) {
                            const laboratory = sample.fields.find((f) => f.identifier === ApiIdentifers.sampleLaboratory);
                            if (laboratory && laboratory.fields) {
                                const extractionPoint = laboratory.fields.find((ff) => ff.identifier === ApiIdentifers.sampleLaboratoryExtractionPoint);
                                if (extractionPoint && extractionPoint.valueNumber != null && usedSamplesExtractionPoints.find((id) => id === extractionPoint.valueNumber) == null) {
                                    usedSamplesExtractionPoints.push(extractionPoint.valueNumber);
                                }
                            }
                        }
                    });
                } else {
                    maxSectionItems = section.fields.length > maxSectionItems ? section.fields.length : maxSectionItems;
                }
            }
        }
        if (inIdentifier === ApiIdentifers.sample) {
            maxSectionItems = usedSamplesExtractionPoints.length;
        }
        return maxSectionItems;
    }

    private sortMaterialsByBlendDesc(lhs: TableDataSetField, rhs: TableDataSetField): number {
        const lhsValue = lhs.fields && lhs.fields.find((f) => f.identifier === ApiIdentifers.materialBlend);
        const rhsValue = rhs.fields && rhs.fields.find((f) => f.identifier === ApiIdentifers.materialBlend);
        if (lhsValue && lhsValue.valueNumber != null && rhsValue && rhsValue.valueNumber != null) {
            if (lhsValue.valueNumber > rhsValue.valueNumber) {
                return -1;
            } else if (lhsValue.valueNumber < rhsValue.valueNumber) {
                return 1;
            } else {
                return 0;
            }
        }
        return -1;
    }

    private sortSamplesByExtractionPoint(lhs: TableDataSetField, rhs: TableDataSetField): number {
        const lhsLaboratory = lhs.fields && lhs.fields.find((f) => f.identifier === ApiIdentifers.sampleLaboratory);
        const rhsLaboratory = rhs.fields && rhs.fields.find((f) => f.identifier === ApiIdentifers.sampleLaboratory);
        if (lhsLaboratory && rhsLaboratory) {
            const lhsExtractionPoint = lhsLaboratory.fields && lhsLaboratory.fields.find((sf) => sf.identifier === ApiIdentifers.sampleLaboratoryExtractionPoint);
            const rhsExtractionPoint = rhsLaboratory.fields && rhsLaboratory.fields.find((sf) => sf.identifier === ApiIdentifers.sampleLaboratoryExtractionPoint);
            if (lhsExtractionPoint && lhsExtractionPoint.valueNumber != null && rhsExtractionPoint && rhsExtractionPoint.valueNumber != null) {
                if (lhsExtractionPoint.valueNumber > rhsExtractionPoint.valueNumber) {
                    return 1;
                } else if (lhsExtractionPoint.valueNumber < rhsExtractionPoint.valueNumber) {
                    return -1;
                } else {
                    return 0;
                }
            }
        }

        return -1;
    }
}

export default ExcelExportProvider;
